/**
 * Logic nodes — comparison, boolean operations.
 */
import type { NodeBehaviour } from '../types';
import { input, output } from '../node/NodeRegistry';

export const And: NodeBehaviour = {
  id: 'logic.and',
  name: 'AND',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'CircleDot',
  description: 'A AND B',
  keywords: ['boolean', 'both'],
  inputs: [
    input('a', 'A', 'bool', false),
    input('b', 'B', 'bool', false),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) { ctx.setOutput('result', !!(ctx.getInput('a') && ctx.getInput('b'))); },
  compile(ctx) {
    return ctx.emit('op:and', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const Or: NodeBehaviour = {
  id: 'logic.or',
  name: 'OR',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'Circle',
  description: 'A OR B',
  keywords: ['boolean', 'either'],
  inputs: [
    input('a', 'A', 'bool', false),
    input('b', 'B', 'bool', false),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) { ctx.setOutput('result', !!(ctx.getInput('a') || ctx.getInput('b'))); },
  compile(ctx) {
    return ctx.emit('op:or', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const Not: NodeBehaviour = {
  id: 'logic.not',
  name: 'NOT',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'Ban',
  description: '!A',
  keywords: ['boolean', 'invert', 'negate'],
  inputs: [input('a', 'A', 'bool', false)],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) { ctx.setOutput('result', !ctx.getInput('a')); },
  compile(ctx) {
    return ctx.emit('op:not', [ctx.resolveInput('a')], 'bool');
  },
};

export const Equal: NodeBehaviour = {
  id: 'logic.equal',
  name: 'Equal',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'Equal',
  description: 'A == B',
  keywords: ['compare', 'same', 'equals'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', ctx.getInput<number>('a') === ctx.getInput<number>('b'));
  },
  compile(ctx) {
    return ctx.emit('op:eq', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const NotEqual: NodeBehaviour = {
  id: 'logic.notEqual',
  name: 'Not Equal',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'EqualNot',
  description: 'A != B',
  keywords: ['compare', 'different'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', ctx.getInput<number>('a') !== ctx.getInput<number>('b'));
  },
  compile(ctx) {
    return ctx.emit('op:neq', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const Greater: NodeBehaviour = {
  id: 'logic.greater',
  name: 'Greater',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'ChevronRight',
  description: 'A > B',
  keywords: ['compare', 'more', 'larger'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) > (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:gt', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const Less: NodeBehaviour = {
  id: 'logic.less',
  name: 'Less',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'ChevronLeft',
  description: 'A < B',
  keywords: ['compare', 'fewer', 'smaller'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) < (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:lt', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const GreaterEqual: NodeBehaviour = {
  id: 'logic.greaterEqual',
  name: 'Greater Equal',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'ChevronsRight',
  description: 'A >= B',
  keywords: ['compare'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) >= (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:gte', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const LessEqual: NodeBehaviour = {
  id: 'logic.lessEqual',
  name: 'Less Equal',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'ChevronsLeft',
  description: 'A <= B',
  keywords: ['compare'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', (ctx.getInput<number>('a') ?? 0) <= (ctx.getInput<number>('b') ?? 0));
  },
  compile(ctx) {
    return ctx.emit('op:lte', [ctx.resolveInput('a'), ctx.resolveInput('b')], 'bool');
  },
};

export const Select: NodeBehaviour = {
  id: 'logic.select',
  name: 'Select',
  category: 'Logic',
  isPure: true,
  color: '#8e44ad',
  icon: 'ToggleRight',
  description: 'Returns A if condition is true, B otherwise.',
  keywords: ['ternary', 'choose', 'pick', 'conditional'],
  inputs: [
    input('condition', 'Condition', 'bool', false),
    input('a', 'True Value', 'f32', 0),
    input('b', 'False Value', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'f32')],
  execute(ctx) {
    ctx.setOutput('result', ctx.getInput<boolean>('condition') ? ctx.getInput('a') : ctx.getInput('b'));
  },
  compile(ctx) {
    return ctx.emit('select', [ctx.resolveInput('condition'), ctx.resolveInput('a'), ctx.resolveInput('b')], 'f32');
  },
};

// ── Validation ──────────────────────────────────────────────────

export const IsNaN: NodeBehaviour = {
  id: 'logic.isNaN',
  name: 'Is NaN',
  category: 'Logic',
  isPure: true,
  color: '#e74c3c',
  icon: 'AlertTriangle',
  description: 'Returns true if the value is NaN.',
  keywords: ['nan', 'invalid', 'not a number', 'check'],
  inputs: [input('a', 'Value', 'f32', 0)],
  outputs: [output('result', 'Is NaN', 'bool')],
  execute(ctx) { ctx.setOutput('result', Number.isNaN(ctx.getInput<number>('a'))); },
};

export const IsInfinity: NodeBehaviour = {
  id: 'logic.isInfinity',
  name: 'Is Infinity',
  category: 'Logic',
  isPure: true,
  color: '#e74c3c',
  icon: 'Infinity',
  description: 'Returns true if the value is +Infinity or -Infinity.',
  keywords: ['infinite', 'overflow', 'check'],
  inputs: [input('a', 'Value', 'f32', 0)],
  outputs: [output('result', 'Is Inf', 'bool')],
  execute(ctx) {
    const v = ctx.getInput<number>('a') ?? 0;
    ctx.setOutput('result', !Number.isFinite(v) && !Number.isNaN(v));
  },
};

export const IsFinite: NodeBehaviour = {
  id: 'logic.isFinite',
  name: 'Is Finite',
  category: 'Logic',
  isPure: true,
  color: '#e74c3c',
  icon: 'CheckCircle',
  description: 'Returns true if the value is finite (not NaN, not Infinity).',
  keywords: ['finite', 'valid', 'check'],
  inputs: [input('a', 'Value', 'f32', 0)],
  outputs: [output('result', 'Is Finite', 'bool')],
  execute(ctx) { ctx.setOutput('result', Number.isFinite(ctx.getInput<number>('a') ?? 0)); },
};

export const IsZero: NodeBehaviour = {
  id: 'logic.isZero',
  name: 'Is Zero',
  category: 'Logic',
  isPure: true,
  color: '#e74c3c',
  icon: 'CircleOff',
  description: 'Returns true if the value is approximately zero.',
  keywords: ['zero', 'empty', 'check', 'epsilon'],
  inputs: [
    input('a', 'Value', 'f32', 0),
    input('eps', 'Epsilon', 'f32', 0.0001),
  ],
  outputs: [output('result', 'Is Zero', 'bool')],
  execute(ctx) {
    ctx.setOutput('result', Math.abs(ctx.getInput<number>('a') ?? 0) < (ctx.getInput<number>('eps') ?? 0.0001));
  },
};

export const LOGIC_NODES: readonly NodeBehaviour[] = [
  And, Or, Not, Equal, NotEqual, Greater, Less, GreaterEqual, LessEqual, Select,
  IsNaN, IsInfinity, IsFinite, IsZero,
];
